/**
 * SanctionListCronManager - User-friendly UI for managing automatic sanction list updates
 *
 * Designed for non-technical business users with:
 * - Plain language explanations
 * - Visual status indicators
 * - Simple time picker instead of cron expressions
 * - Clear action buttons
 * - Helpful guidance throughout
 */

import { useState, useEffect, useCallback } from "react";
import { format, parseISO, isAfter, addDays } from "date-fns";
import complianceService from "../../services/compliance";

const isDev = process.env.NODE_ENV === "development";

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: {
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  section: {
    background: "#fff",
    border: "1px solid rgba(148, 163, 184, 0.2)",
    borderRadius: "12px",
    padding: "1.5rem",
    marginBottom: "1.5rem",
    transition: "all 0.2s ease",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1.25rem",
    paddingBottom: "1rem",
    borderBottom: "1px solid rgba(148, 163, 184, 0.15)",
  },
  sectionTitle: {
    fontSize: "1.125rem",
    fontWeight: 600,
    color: "#1e293b",
    margin: 0,
    letterSpacing: "-0.01em",
  },
  sectionSubtitle: {
    fontSize: "0.8125rem",
    color: "#64748b",
    marginTop: "0.25rem",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.375rem",
    padding: "0.375rem 0.75rem",
    borderRadius: "6px",
    fontSize: "0.8125rem",
    fontWeight: 500,
  },
  statusActive: {
    backgroundColor: "rgba(34, 197, 94, 0.1)",
    color: "#16a34a",
    border: "1px solid rgba(34, 197, 94, 0.2)",
  },
  statusDisabled: {
    backgroundColor: "rgba(148, 163, 184, 0.1)",
    color: "#64748b",
    border: "1px solid rgba(148, 163, 184, 0.2)",
  },
  statusRunning: {
    backgroundColor: "rgba(59, 130, 246, 0.1)",
    color: "#3b82f6",
    border: "1px solid rgba(59, 130, 246, 0.2)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "1rem",
  },
  infoCard: {
    padding: "1rem",
    background: "rgba(59, 130, 246, 0.03)",
    border: "1px solid rgba(59, 130, 246, 0.1)",
    borderRadius: "8px",
  },
  infoLabel: {
    fontSize: "0.75rem",
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "0.25rem",
  },
  infoValue: {
    fontSize: "0.9375rem",
    fontWeight: 600,
    color: "#1e293b",
  },
  formGroup: {
    marginBottom: "1.25rem",
  },
  label: {
    display: "block",
    fontSize: "0.8125rem",
    fontWeight: 500,
    color: "#475569",
    marginBottom: "0.5rem",
  },
  input: {
    width: "100%",
    padding: "0.625rem 0.875rem",
    border: "1px solid rgba(148, 163, 184, 0.3)",
    borderRadius: "8px",
    fontSize: "0.875rem",
    color: "#1e293b",
    outline: "none",
    transition: "border-color 0.15s ease",
    fontFamily: "monospace",
  },
  button: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.625rem 1rem",
    borderRadius: "8px",
    fontSize: "0.875rem",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    transition: "all 0.15s ease",
  },
  buttonPrimary: {
    backgroundColor: "#0f172a",
    color: "#fff",
  },
  buttonSecondary: {
    backgroundColor: "transparent",
    color: "#475569",
    border: "1px solid rgba(148, 163, 184, 0.3)",
  },
  buttonDanger: {
    backgroundColor: "rgba(239, 68, 68, 0.1)",
    color: "#dc2626",
    border: "1px solid rgba(239, 68, 68, 0.2)",
  },
  buttonSuccess: {
    backgroundColor: "rgba(34, 197, 94, 0.1)",
    color: "#16a34a",
    border: "1px solid rgba(34, 197, 94, 0.2)",
  },
  buttonGroup: {
    display: "flex",
    gap: "0.75rem",
    flexWrap: "wrap",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    textAlign: "left",
    padding: "0.75rem",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: "1px solid rgba(148, 163, 184, 0.15)",
  },
  tableCell: {
    padding: "0.75rem",
    fontSize: "0.8125rem",
    color: "#1e293b",
    borderBottom: "1px solid rgba(148, 163, 184, 0.1)",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "0.25rem 0.5rem",
    borderRadius: "4px",
    fontSize: "0.75rem",
    fontWeight: 500,
  },
  badgeSuccess: {
    backgroundColor: "rgba(34, 197, 94, 0.1)",
    color: "#16a34a",
  },
  badgeError: {
    backgroundColor: "rgba(239, 68, 68, 0.1)",
    color: "#dc2626",
  },
  badgeWarning: {
    backgroundColor: "rgba(245, 158, 11, 0.1)",
    color: "#d97706",
  },
  error: {
    padding: "0.75rem 1rem",
    backgroundColor: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.2)",
    borderRadius: "8px",
    color: "#dc2626",
    fontSize: "0.8125rem",
    marginBottom: "1rem",
  },
  success: {
    padding: "0.75rem 1rem",
    backgroundColor: "rgba(34, 197, 94, 0.1)",
    border: "1px solid rgba(34, 197, 94, 0.2)",
    borderRadius: "8px",
    color: "#16a34a",
    fontSize: "0.8125rem",
    marginBottom: "1rem",
  },
  loading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem",
    color: "#64748b",
    fontSize: "0.875rem",
  },
  emptyState: {
    textAlign: "center",
    padding: "3rem 1rem",
    color: "#64748b",
  },
  helpText: {
    fontSize: "0.75rem",
    color: "#94a3b8",
    marginTop: "0.25rem",
    fontStyle: "italic",
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// Convert cron expression to human-readable time
const cronToTime = (cronExpression) => {
  try {
    const parts = cronExpression.split(" ");
    if (parts.length !== 5) return null;

    const hour = parseInt(parts[1], 10);
    const minute = parseInt(parts[0], 10);

    if (isNaN(hour) || isNaN(minute)) return null;

    const time = new Date();
    time.setUTCHours(hour, minute, 0, 0);

    return {
      hour: hour.toString().padStart(2, "0"),
      minute: minute.toString().padStart(2, "0"),
      display: format(time, "h:mm a"),
    };
  } catch (error) {
    return null;
  }
};

// Convert time picker values to cron expression
const timeToCron = (hour, minute) => {
  return `${minute || 0} ${hour || 0} * * *`;
};

// Calculate next run time
const calculateNextRun = (cronExpression, timezone = "GMT") => {
  try {
    const time = cronToTime(cronExpression);
    if (!time) return null;

    const now = new Date();
    const nextRun = new Date();

    nextRun.setUTCHours(
      parseInt(time.hour, 10),
      parseInt(time.minute, 10),
      0,
      0
    );

    // If time has passed today, schedule for tomorrow
    if (nextRun <= now) {
      nextRun.setUTCDate(nextRun.getUTCDate() + 1);
    }

    return nextRun;
  } catch (error) {
    return null;
  }
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const SanctionListCronManager = () => {
  const [cronStatus, setCronStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isTriggering, setIsTriggering] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [cronExpression, setCronExpression] = useState("0 0 * * *");
  const [enabled, setEnabled] = useState(true);
  const [executionHistory, setExecutionHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // User-friendly time picker state
  const [updateHour, setUpdateHour] = useState("00");
  const [updateMinute, setUpdateMinute] = useState("00");

  // Fetch cron status
  const fetchCronStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await complianceService.getSanctionListCronStatus();
      const data = response?.data || {};

      setCronStatus(data);
      const schedule = data.schedule || "0 0 * * *";
      setCronExpression(schedule);
      setEnabled(data.enabled !== false);

      // Convert cron to time picker values
      const time = cronToTime(schedule);
      if (time) {
        setUpdateHour(time.hour);
        setUpdateMinute(time.minute);
      }

      if (data.executionHistory) {
        setExecutionHistory(data.executionHistory);
      }
    } catch (err) {
      console.error("[SanctionListCronManager] Failed to fetch status:", err);
      setError("Failed to load cron job status. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCronStatus();

    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchCronStatus();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchCronStatus]);

  // Handle time change
  const handleTimeChange = (hour, minute) => {
    setUpdateHour(hour);
    setUpdateMinute(minute);
    const newCron = timeToCron(parseInt(hour, 10), parseInt(minute, 10));
    setCronExpression(newCron);
  };

  // Save configuration
  const handleSaveConfig = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      // Convert time picker to cron expression
      const schedule = timeToCron(
        parseInt(updateHour, 10),
        parseInt(updateMinute, 10)
      );

      await complianceService.updateSanctionListCronConfig({
        schedule,
        enabled,
      });

      setSuccess(
        "Automatic update settings saved successfully. Changes will take effect after the server restarts."
      );
      await fetchCronStatus();

      setTimeout(() => setSuccess(null), 8000);
    } catch (err) {
      console.error("[SanctionListCronManager] Save failed:", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save settings. Please try again.";
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  // Manual trigger
  const handleTrigger = async () => {
    if (
      !window.confirm(
        "Check all sanction lists for updates now? This will verify if any lists have new information available."
      )
    ) {
      return;
    }

    try {
      setIsTriggering(true);
      setError(null);
      setSuccess(null);

      const response = await complianceService.triggerSanctionListCron();
      const data = response?.data || {};

      const updatedCount = data.updated || 0;
      const skippedCount = data.skipped || 0;
      const errorCount = data.errors || 0;

      if (updatedCount > 0) {
        setSuccess(
          `✓ Update complete! ${updatedCount} list(s) were updated with new information. ${skippedCount} list(s) were already up to date.`
        );
      } else if (errorCount > 0) {
        setError(
          `Update completed with ${errorCount} error(s). ${skippedCount} list(s) were checked. Please review the logs for details.`
        );
      } else {
        setSuccess(
          `✓ All lists are up to date. No changes found in ${skippedCount} list(s) checked.`
        );
      }

      // Refresh status after a delay
      setTimeout(() => {
        fetchCronStatus();
      }, 2000);

      setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 10000);
    } catch (err) {
      console.error("[SanctionListCronManager] Trigger failed:", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to check for updates. Please try again.";
      setError(message);
    } finally {
      setIsTriggering(false);
    }
  };

  const nextRunTime = calculateNextRun(cronExpression, cronStatus?.timezone);

  if (loading && !cronStatus) {
    return (
      <div style={styles.loading}>
        <span>Loading cron job status...</span>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Status Section */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <h2 style={styles.sectionTitle}>Automatic Update Status</h2>
            <p style={styles.sectionSubtitle}>
              System automatically checks sanction lists for new information
            </p>
          </div>
          <div
            style={{
              ...styles.statusBadge,
              ...(cronStatus?.isRunning
                ? styles.statusRunning
                : enabled
                ? styles.statusActive
                : styles.statusDisabled),
            }}
          >
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: cronStatus?.isRunning
                  ? "#3b82f6"
                  : enabled
                  ? "#16a34a"
                  : "#94a3b8",
                display: "inline-block",
              }}
            />
            {cronStatus?.isRunning
              ? "Checking Now"
              : enabled
              ? "Enabled"
              : "Disabled"}
          </div>
        </div>

        <div style={styles.grid}>
          <div style={styles.infoCard}>
            <div style={styles.infoLabel}>Update Schedule</div>
            <div style={styles.infoValue}>
              {cronToTime(cronExpression)?.display || "Daily"}
            </div>
            <div style={styles.helpText}>
              {cronExpression === "0 0 * * *"
                ? "Every day at midnight (GMT)"
                : "Daily at specified time"}
            </div>
          </div>

          {nextRunTime && (
            <div style={styles.infoCard}>
              <div style={styles.infoLabel}>Next Automatic Check</div>
              <div style={styles.infoValue}>
                {format(nextRunTime, "MMM d, yyyy")}
              </div>
              <div style={styles.helpText}>
                {format(nextRunTime, "h:mm a")} GMT
                {isAfter(nextRunTime, new Date()) && (
                  <span style={{ display: "block", marginTop: "0.25rem" }}>
                    (in{" "}
                    {Math.round((nextRunTime - new Date()) / 1000 / 60 / 60)}{" "}
                    hours)
                  </span>
                )}
              </div>
            </div>
          )}

          {cronStatus?.lastRun && (
            <div style={styles.infoCard}>
              <div style={styles.infoLabel}>Last Check</div>
              <div style={styles.infoValue}>
                {format(parseISO(cronStatus.lastRun), "MMM d, yyyy")}
              </div>
              <div style={styles.helpText}>
                {format(parseISO(cronStatus.lastRun), "h:mm a")} GMT
                {cronStatus.lastRunStatus && (
                  <span style={{ display: "block", marginTop: "0.25rem" }}>
                    Status: {cronStatus.lastRunStatus}
                  </span>
                )}
              </div>
            </div>
          )}

          {cronStatus?.totalExecutions !== undefined && (
            <div style={styles.infoCard}>
              <div style={styles.infoLabel}>Total Checks</div>
              <div style={styles.infoValue}>{cronStatus.totalExecutions}</div>
              <div style={styles.helpText}>Automatic checks performed</div>
            </div>
          )}
        </div>
      </div>

      {/* Configuration Section */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <h2 style={styles.sectionTitle}>Update Settings</h2>
            <p style={styles.sectionSubtitle}>
              Configure when the system automatically checks for updates
            </p>
          </div>
        </div>

        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}

        <div style={styles.formGroup}>
          <label style={styles.label}>
            What time should we check for updates?
          </label>
          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              alignItems: "center",
              marginTop: "0.5rem",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <select
                style={{
                  ...styles.input,
                  width: "80px",
                  padding: "0.625rem 0.5rem",
                }}
                value={updateHour}
                onChange={(e) => handleTimeChange(e.target.value, updateMinute)}
              >
                {Array.from({ length: 24 }, (_, i) => {
                  const hour = i.toString().padStart(2, "0");
                  return (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  );
                })}
              </select>
              <span style={{ fontSize: "0.875rem", color: "#64748b" }}>:</span>
              <select
                style={{
                  ...styles.input,
                  width: "80px",
                  padding: "0.625rem 0.5rem",
                }}
                value={updateMinute}
                onChange={(e) => handleTimeChange(updateHour, e.target.value)}
              >
                {["00", "15", "30", "45"].map((min) => (
                  <option key={min} value={min}>
                    {min}
                  </option>
                ))}
              </select>
              <span
                style={{
                  fontSize: "0.875rem",
                  color: "#64748b",
                  marginLeft: "0.5rem",
                }}
              >
                GMT (Greenwich Mean Time)
              </span>
            </div>
          </div>
          <div style={styles.helpText}>
            The system will check all sanction lists for new information at this
            time every day. We recommend midnight (00:00) or early morning hours
            to avoid peak usage times.
          </div>
        </div>

        <div style={styles.formGroup}>
          <label
            style={{
              ...styles.label,
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              style={{
                marginRight: "0.75rem",
                width: "18px",
                height: "18px",
                cursor: "pointer",
              }}
            />
            <div>
              <div style={{ fontWeight: 500, marginBottom: "0.25rem" }}>
                Enable automatic daily updates
              </div>
              <div
                style={{
                  fontSize: "0.8125rem",
                  color: "#64748b",
                  fontWeight: 400,
                }}
              >
                When enabled, the system will automatically check for updates at
                the scheduled time. When disabled, you'll need to check manually
                using the "Check Now" button.
              </div>
            </div>
          </label>
        </div>

        <div style={styles.buttonGroup}>
          <button
            style={{
              ...styles.button,
              ...styles.buttonPrimary,
              opacity: isSaving ? 0.7 : 1,
              cursor: isSaving ? "not-allowed" : "pointer",
            }}
            onClick={handleSaveConfig}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span
                  style={{
                    width: "14px",
                    height: "14px",
                    border: "2px solid currentColor",
                    borderTopColor: "transparent",
                    borderRadius: "50%",
                    display: "inline-block",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
                Saving...
              </>
            ) : (
              "Save Settings"
            )}
          </button>
          <button
            style={{
              ...styles.button,
              ...styles.buttonSuccess,
              opacity: isTriggering ? 0.7 : 1,
              cursor: isTriggering ? "not-allowed" : "pointer",
            }}
            onClick={handleTrigger}
            disabled={isTriggering}
          >
            {isTriggering ? (
              <>
                <span
                  style={{
                    width: "14px",
                    height: "14px",
                    border: "2px solid currentColor",
                    borderTopColor: "transparent",
                    borderRadius: "50%",
                    display: "inline-block",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
                Checking...
              </>
            ) : (
              <>
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{ marginRight: "0.25rem" }}
                >
                  <path d="M8 3v10M3 8h10" />
                </svg>
                Check Now
              </>
            )}
          </button>
        </div>

        <div
          style={{
            marginTop: "1rem",
            padding: "0.75rem",
            backgroundColor: "rgba(59, 130, 246, 0.05)",
            border: "1px solid rgba(59, 130, 246, 0.2)",
            borderRadius: "6px",
            fontSize: "0.8125rem",
            color: "#475569",
          }}
        >
          <strong>💡 Tip:</strong> Use "Check Now" to immediately verify if any
          sanction lists have new information, without waiting for the scheduled
          time.
        </div>
      </div>

      {/* Execution History Section */}
      {executionHistory.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div>
              <h2 style={styles.sectionTitle}>Update History</h2>
              <p style={styles.sectionSubtitle}>
                Recent automatic checks and their results
              </p>
            </div>
            <button
              style={{
                ...styles.button,
                ...styles.buttonSecondary,
                fontSize: "0.8125rem",
              }}
              onClick={() => setShowHistory(!showHistory)}
            >
              {showHistory ? "Hide History" : "View History"}
            </button>
          </div>

          {showHistory && (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>Date & Time</th>
                    <th style={styles.tableHeader}>Result</th>
                    <th style={styles.tableHeader}>Lists Updated</th>
                    <th style={styles.tableHeader}>Already Up to Date</th>
                    <th style={styles.tableHeader}>Issues</th>
                    <th style={styles.tableHeader}>Time Taken</th>
                  </tr>
                </thead>
                <tbody>
                  {executionHistory.map((execution, index) => (
                    <tr key={index}>
                      <td style={styles.tableCell}>
                        {execution.timestamp
                          ? format(
                              parseISO(execution.timestamp),
                              "MMM d, yyyy 'at' h:mm a"
                            )
                          : "N/A"}
                      </td>
                      <td style={styles.tableCell}>
                        <span
                          style={{
                            ...styles.badge,
                            ...(execution.status === "success"
                              ? styles.badgeSuccess
                              : execution.status === "error"
                              ? styles.badgeError
                              : styles.badgeWarning),
                          }}
                        >
                          {execution.status === "success"
                            ? "✓ Success"
                            : execution.status === "error"
                            ? "✗ Error"
                            : "⚠ Warning"}
                        </span>
                      </td>
                      <td style={styles.tableCell}>
                        <strong style={{ color: "#16a34a" }}>
                          {execution.updated || 0}
                        </strong>
                      </td>
                      <td style={styles.tableCell}>{execution.skipped || 0}</td>
                      <td style={styles.tableCell}>
                        {execution.errors > 0 ? (
                          <span style={{ color: "#dc2626" }}>
                            {execution.errors}
                          </span>
                        ) : (
                          <span style={{ color: "#16a34a" }}>None</span>
                        )}
                      </td>
                      <td style={styles.tableCell}>
                        {execution.duration
                          ? `${execution.duration} seconds`
                          : "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Help Section */}
      <div
        style={{
          ...styles.section,
          backgroundColor: "rgba(249, 250, 251, 0.8)",
          borderColor: "rgba(148, 163, 184, 0.3)",
        }}
      >
        <div style={styles.sectionHeader}>
          <div>
            <h2 style={{ ...styles.sectionTitle, fontSize: "1rem" }}>
              How It Works
            </h2>
          </div>
        </div>
        <div
          style={{ fontSize: "0.875rem", color: "#475569", lineHeight: "1.6" }}
        >
          <p style={{ marginBottom: "0.75rem" }}>
            <strong>Automatic Updates:</strong> The system automatically checks
            all configured sanction lists (like the UN Security Council list) at
            the scheduled time each day. If new information is found, it's
            downloaded and stored securely.
          </p>
          <p style={{ marginBottom: "0.75rem" }}>
            <strong>Manual Check:</strong> Use the "Check Now" button to
            immediately verify if any lists have updates, without waiting for
            the scheduled time. This is useful when you need to ensure you have
            the latest information right away.
          </p>
          <p>
            <strong>Time Zone:</strong> All times are shown in GMT (Greenwich
            Mean Time). This ensures consistent scheduling regardless of your
            local time zone.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SanctionListCronManager;
